/**
 * Two-click loading for video embeds in community bodies (customer decision P9,
 * 2026-10-12): "no unnecessary third-party requests before release".
 *
 * The backend's `CommunitySanitizer` lets an `<iframe>` through only for the
 * hosts in `data/config/community_config.json` (YouTube, YouTube-nocookie,
 * Vimeo). Rendered as-is, such an iframe contacts the provider the moment a post
 * is opened. So before a body ever reaches the DOM, `deferCommunityVideoEmbeds`
 * swaps every iframe for an inert placeholder of the same size that carries the
 * original attributes in a data attribute; `CommunityVideoEmbedsDirective`
 * labels the placeholder and puts the real iframe back on the reader's click.
 *
 * Parsing happens in a `<template>` element: its content lives in an inert
 * document, so nothing parsed there loads — neither the iframe nor an `<img>` of
 * the same body (a detached `<div>` of the live document would fetch images).
 *
 * YouTube is restored on `www.youtube-nocookie.com` — YouTube's "privacy-
 * enhanced mode": the player sets no tracking cookies until the video is played.
 * It is the same player and the backend allowlists the host, so nothing is lost;
 * a body saved before the editor produced nocookie links still gets it.
 */

/** Marker attribute on the placeholder; its value is the provider key. */
export const COMMUNITY_VIDEO_ATTR = 'data-community-video';
/** JSON of the original iframe's (allowed) attributes. */
export const COMMUNITY_VIDEO_ATTRS_ATTR = 'data-community-video-attrs';
/** Human-readable provider name ("YouTube", "Vimeo", or the host). */
export const COMMUNITY_VIDEO_LABEL_ATTR = 'data-community-video-label';

/** sessionStorage key of the "always load this session" flag. Per provider, per browser session, no cookie. */
export function communityVideoAutoKey(provider: string): string {
  return `community.video_embed.auto:${provider}`;
}

export interface CommunityVideoProvider {
  /** Stable key — `youtube`, `vimeo`, or the host for an embed host added to the config later. */
  key: string;
  /** Name shown to the reader. */
  label: string;
}

const KNOWN_PROVIDERS: { key: string; label: string; hosts: string[] }[] = [
  { key: 'youtube', label: 'YouTube', hosts: ['www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com', 'youtube-nocookie.com'] },
  { key: 'vimeo', label: 'Vimeo', hosts: ['player.vimeo.com', 'vimeo.com'] }
];

/**
 * Only these attributes are carried over to the restored iframe — the set the
 * backend sanitizer allows or sets itself (`loading`, `sandbox`,
 * `referrerpolicy`). Anything else cannot be in a stored body anyway.
 */
const IFRAME_ATTRIBUTES = [
  'src', 'width', 'height', 'title', 'allowfullscreen', 'frameborder',
  'loading', 'sandbox', 'referrerpolicy', 'allow'
];

/** The provider of an embed src, or null when it is not an https URL. */
export function communityVideoProvider(src: string | null | undefined): CommunityVideoProvider | null {
  let host: string;
  try {
    const url = new URL(String(src ?? ''));
    if (url.protocol !== 'https:') return null;
    host = url.hostname.toLowerCase();
  } catch {
    return null;
  }
  const known = KNOWN_PROVIDERS.find(p => p.hosts.includes(host));
  if (known) return { key: known.key, label: known.label };
  // A host added to community_config.json without a code change: still deferred,
  // named after its host.
  return { key: host, label: host };
}

/** `www.youtube.com/embed/…` → `www.youtube-nocookie.com/embed/…`; every other src unchanged. */
export function communityVideoPrivacySrc(src: string): string {
  return src.replace(/^https:\/\/(?:www\.)?youtube\.com\/embed\//i, 'https://www.youtube-nocookie.com/embed/');
}

function positiveInt(value: string | null): number | null {
  if (!value) return null;
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Replaces every `<iframe>` of a body with an inert placeholder. Everything else
 * in the body is returned untouched (a body without an iframe is returned as
 * the very same string, without a parse round-trip).
 */
export function deferCommunityVideoEmbeds(html: string | null | undefined): string {
  if (!html) return '';
  if (!/<iframe\b/i.test(html) || typeof document === 'undefined') return html;

  const template = document.createElement('template');
  template.innerHTML = html;
  const frames = Array.from(template.content.querySelectorAll('iframe'));
  for (const frame of frames) {
    const provider = communityVideoProvider(frame.getAttribute('src'));
    if (!provider) {
      // Not an https embed — the backend never stores one; drop rather than load it.
      frame.remove();
      continue;
    }
    frame.replaceWith(buildPlaceholder(frame, provider));
  }
  return template.innerHTML;
}

function buildPlaceholder(frame: HTMLIFrameElement, provider: CommunityVideoProvider): HTMLElement {
  const doc = frame.ownerDocument;
  const attrs: Record<string, string> = {};
  for (const name of IFRAME_ATTRIBUTES) {
    const value = frame.getAttribute(name);
    if (value !== null) attrs[name] = value;
  }

  const box = doc.createElement('div');
  box.className = 'community-video';
  box.setAttribute(COMMUNITY_VIDEO_ATTR, provider.key);
  box.setAttribute(COMMUNITY_VIDEO_LABEL_ATTR, provider.label);
  box.setAttribute(COMMUNITY_VIDEO_ATTRS_ATTR, JSON.stringify(attrs));

  // Same footprint as the iframe would have had: its width (capped by CSS at
  // 100 %) and its aspect ratio, 16:9 when the markup names no dimensions.
  const width = positiveInt(frame.getAttribute('width'));
  const height = positiveInt(frame.getAttribute('height'));
  const styles: string[] = [];
  if (width) styles.push(`width:${width}px`);
  if (width && height) styles.push(`aspect-ratio:${width} / ${height}`);
  if (styles.length) box.setAttribute('style', styles.join(';'));

  // Static skeleton; the directive fills in the (translated) texts. No <img>, no
  // provider thumbnail — fetching one would be exactly the request we avoid.
  box.innerHTML =
    '<button type="button" class="community-video__play">' +
      '<span class="community-video__icon" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" width="28" height="28" focusable="false"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>' +
      '</span>' +
      '<span class="community-video__provider"></span>' +
      '<span class="community-video__text"></span>' +
    '</button>' +
    '<div class="community-video__footer">' +
      '<label class="community-video__auto-label">' +
        '<input type="checkbox" class="community-video__auto"> ' +
        '<span class="community-video__auto-text"></span>' +
      '</label>' +
      '<a class="community-video__privacy" href="#"></a>' +
    '</div>';
  const providerName = box.querySelector('.community-video__provider');
  if (providerName) providerName.textContent = provider.label;
  return box;
}

/** Builds the real iframe back from a placeholder (for the click). Null when the placeholder is malformed. */
export function communityVideoIframe(placeholder: Element): HTMLIFrameElement | null {
  let attrs: Record<string, string>;
  try {
    attrs = JSON.parse(placeholder.getAttribute(COMMUNITY_VIDEO_ATTRS_ATTR) || '{}');
  } catch {
    return null;
  }
  const src = attrs['src'];
  if (!src || !communityVideoProvider(src)) return null;

  const frame = placeholder.ownerDocument.createElement('iframe');
  for (const name of IFRAME_ATTRIBUTES) {
    const value = attrs[name];
    if (typeof value === 'string') frame.setAttribute(name, value);
  }
  frame.setAttribute('src', communityVideoPrivacySrc(src));

  // Keep the footprint of the placeholder: never wider than the post, and the
  // markup's aspect ratio (16:9 without one) instead of the browser's 300×150.
  const width = positiveInt(attrs['width'] ?? null);
  const height = positiveInt(attrs['height'] ?? null);
  frame.style.maxWidth = '100%';
  frame.style.border = '0';
  if (!width) frame.style.width = '100%';
  frame.style.aspectRatio = width && height ? `${width} / ${height}` : '16 / 9';
  frame.style.height = 'auto';
  return frame;
}
